// Modules
import { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useBasicModal } from './useBasicModal';
import {
  useGetNotificationsQuery,
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
  useDeleteNotificationMutation,
  useDeleteAllNotificationsMutation,
} from 'features/notifications/notificationsApi';
import { useSocket, useSocketEvent } from './useSocket';
import { Notification as NotificationType } from 'features/notifications/notificationsTypes';

const useNotifications = () => {
  const { t } = useTranslation('common');
  const { socket, isConnected } = useSocket();
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Disable RTK Query automatic refetching - we use WebSocket for real-time updates
  const {
    data: notificationsResponse,
    isLoading: isFetchingNotifications,
    refetch: refetchNotifications,
  } = useGetNotificationsQuery(undefined, {
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const [markAsRead, { isLoading: isMarkingOne }] = useMarkAsReadMutation();
  const [markAllAsRead, { isLoading: isMarkingAll }] = useMarkAllAsReadMutation();
  const [deleteNotification, { isLoading: isDeletingOne }] = useDeleteNotificationMutation();
  const [deleteAllNotifications, { isLoading: isDeletingAll }] = useDeleteAllNotificationsMutation();

  const notifications = notificationsResponse?.data ?? [];
  const pagination = notificationsResponse?.pagination;

  const unreadNotifications = notifications.filter((n) => !n.read);

  const {
    showModal: showNotificationsModal,
    handleCloseModal: handleCloseNotificationsModal,
    handleOpenModal: handleOpenNotificationsModal,
  } = useBasicModal();

  const handleMarkAsRead = async (notificationId: string, skipToast = false) => {
    try {
      await markAsRead(notificationId).unwrap();

      if (!skipToast) {
        toast.success(t('NOTIFICATION_UPDATE_SUCCESS'));
      }
    } catch (error: any) {
      console.log(error);
      // Skip showing error toast if it's a 403 permission error (already handled globally)
      if (error?.status !== 403) {
        toast.error(t('NOTIFICATION_UPDATE_FAIL'));
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead().unwrap();

      toast.success(t('NOTIFICATION_UPDATE_SUCCESS'));
    } catch (error: any) {
      console.log(error);
      // Skip showing error toast if it's a 403 permission error (already handled globally)
      if (error?.status !== 403) {
        toast.error(t('NOTIFICATION_UPDATE_FAIL'));
      }
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      await deleteNotification(notificationId).unwrap();

      toast.success(t('NOTIFICATION_DELETE_SUCCESS'));
    } catch (error: any) {
      console.log(error);
      // Skip showing error toast if it's a 403 permission error (already handled globally)
      if (error?.status !== 403) {
        toast.error(t('NOTIFICATION_DELETE_FAIL'));
      }
    }
  };

  const handleDeleteAllNotifications = async () => {
    try {
      await deleteAllNotifications().unwrap();

      toast.success(t('NOTIFICATION_DELETE_ALL_SUCCESS'));
    } catch (error: any) {
      console.log(error);
      // Skip showing error toast if it's a 403 permission error (already handled globally)
      if (error?.status !== 403) {
        toast.error(t('NOTIFICATION_DELETE_ALL_FAIL'));
      }
    }
  };

  // Stable refetch callback to avoid dependency issues
  const stableRefetch = useCallback(() => {
    refetchNotifications();
  }, [refetchNotifications]);

  // Handle new notifications via socket
  const handleNewNotification = useCallback(
    (notification: NotificationType) => {
      try {
        stableRefetch();

        toast.success(notification.message);

        if (Notification.permission === 'granted') {
          new Notification('New Notification', {
            body: notification.message,
            icon: '/favicon.ico',
            tag: notification._id,
          });
        }
      } catch (error) {
        console.error('Error handling new notification:', error);
      }
    },
    [stableRefetch],
  );

  // Subscribe to notification events
  useSocketEvent('new_notification', handleNewNotification, [handleNewNotification]);
  useSocketEvent('notification_marked_read', stableRefetch, [stableRefetch]);
  useSocketEvent('all_notifications_marked_read', stableRefetch, [stableRefetch]);

  // Manual polling fallback when WebSocket is not connected
  useEffect(() => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Only poll if WebSocket is not connected
    if (!isConnected) {
      pollingIntervalRef.current = setInterval(() => {
        stableRefetch();
      }, 60000); // Poll every 60 seconds as fallback
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isConnected, stableRefetch]);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  return {
    notifications,
    unreadNotifications,
    isFetchingNotifications,
    isUpdatingNotifications: isMarkingOne || isMarkingAll || isDeletingOne || isDeletingAll,
    handleMarkAsRead,
    handleMarkAllAsRead,
    handleDeleteNotification,
    handleDeleteAllNotifications,

    // Pagination
    pagination,
    refetchNotifications,

    showNotificationsModal,
    handleCloseNotificationsModal,
    handleOpenNotificationsModal,

    // WebSocket status
    isConnected,
    socket,
  };
};

export default useNotifications;
